import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { PersonaSwitcher } from "./PersonaSwitcher";
import { GuidedDemo } from "./GuidedDemo";
import { FileDown, FileText, Inbox, MapPin, Network, Presentation, Scale, ShieldCheck, UserCheck, Users } from "lucide-react";
import { Activity, BarChart3, Building2, Globe2, LayoutGrid, Plus, RotateCcw, Settings as SettingsIcon, ShieldAlert } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { API, api, safe } from "@/lib/api";
import { toast } from "sonner";

const CAPS = [["home", "OmniFi Home", "/"], ["executive", "Executive", "/executive"], ["lending", "Credit Lending (XAI)", "/lending"], ["wire", "Wire Ops & AML", "/task/task-wire-ops"], ["compliance", "Cross-Border Compliance", "/task/task-compliance"], ["kyc", "KYC", "/task/task-kyc"], ["aml", "AML Intel", "/aml"], ["grc", "GRC & Audit", "/grc"]];

function CapabilityTabs() {
  const { pathname } = useLocation();
  const { tasks } = useApp();
  const t = tasks.find((x) => pathname === `/task/${x.id}`);
  const active = pathname === "/" ? "home" : pathname === "/executive" ? "executive" : pathname === "/grc" ? "grc" : pathname === "/aml" ? "aml" : pathname === "/lending" || t?.type === "loan" ? "lending" : t?.type || "";
  return (
    <nav className="hidden lg:flex items-center gap-0.5 ml-6 p-1 rounded-xl bg-slate-100/80 ring-1 ring-slate-200/60" data-testid="capability-tabs">
      {CAPS.map(([k, label, to]) => (
        <NavLink key={k} to={to} data-testid={`capability-tab-${k}`}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${active === k ? "bg-white text-sky-700 shadow-[0_1px_3px_rgba(15,23,42,0.12)]" : "text-slate-500 hover:text-slate-900"}`}>{label}</NavLink>
      ))}
    </nav>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <span data-testid="live-indicator" className="hidden min-[1800px]:flex items-center gap-2 whitespace-nowrap text-[11px] num text-emerald-800 px-3 py-1.5 rounded-full bg-emerald-50 ring-1 ring-emerald-200">
      <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-60 animate-ping" /><span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" /></span>
      LIVE · {now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </span>
  );
}

function ApprovalsBadge() {
  const { personaId } = useApp();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  useEffect(() => {
    const load = () => api.get("/approvals/count").then((r) => setC(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("omnifi:approvals", load);
    return () => { clearInterval(t); window.removeEventListener("omnifi:approvals", load); };
  }, [personaId]);
  if (!c?.approver) return null;
  return (
    <button data-testid="approvals-badge" onClick={() => nav("/approvals")} className="relative btn-ghost">
      <Inbox className="w-3.5 h-3.5" /><span className="hidden sm:inline">Approvals</span>
      {c.total > 0 && <span data-testid="approvals-badge-count" className="num min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-b from-rose-500 to-red-600 text-white text-[10px] font-semibold flex items-center justify-center shadow-[0_2px_6px_rgba(220,38,38,0.4)]">{c.total}</span>}
    </button>
  );
}

const MOBILE_NAV = [["/", "Home"], ["/executive", "Executive"], ["/lending", "Lending"], ["/task/task-wire-ops", "Wires"], ["/task/task-compliance", "Compliance"], ["/task/task-kyc", "KYC"], ["/aml", "AML Intel"], ["/fraud", "Fraud engine"], ["/monitor", "Live monitoring"], ["/market-comps", "Market comps"], ["/governance", "Governance"], ["/systems", "Systems"], ["/policy", "Policy"], ["/grc", "GRC"], ["/approvals", "Approvals"], ["/credit-reports", "Credit reports"], ["/settings", "Settings"], ["/admin", "Admin"]];

function MobileNav() {
  return (
    <nav data-testid="mobile-nav" className="md:hidden flex gap-1 px-3 py-2 overflow-x-auto thin-scroll border-b border-slate-200/80 bg-white/90 shrink-0">
      {MOBILE_NAV.map(([to, l]) => (
        <NavLink key={to} to={to} end data-testid={`mobile-nav-${l.toLowerCase()}`} className={({ isActive }) => `whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full ${isActive ? "bg-sky-600 text-white" : "text-slate-600 bg-slate-100"}`}>{l}</NavLink>
      ))}
    </nav>
  );
}

function Header({ onDemo }) {
  return (
    <header className="h-16 border-b border-slate-200/80 bg-white/85 backdrop-blur-md flex items-center justify-between px-5 shrink-0 relative z-40 shadow-[0_1px_0_rgba(15,23,42,0.02)]">
      <div className="flex items-center gap-3">
        <img data-testid="brand-logo" src="/cloudscore-logo.png" alt="CloudScore" className="h-8 w-auto" />
        <span data-testid="app-name" className="hidden sm:inline font-display text-xl font-extrabold tracking-tight text-slate-900 border-l border-slate-200 pl-3">Omni<span className="text-sky-600">Fi</span></span>
        <CapabilityTabs />
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <LiveClock />
        <ApprovalsBadge />
        <button data-testid="guided-demo-toggle" onClick={onDemo} className="hidden md:flex btn-primary whitespace-nowrap"><Presentation className="w-3.5 h-3.5" /><span className="hidden 2xl:inline">Guided walkthrough</span><span className="2xl:hidden">Guide</span></button>
        <PersonaSwitcher />
      </div>
    </header>
  );
}

const navCls = ({ isActive }) => `relative flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-colors ${isActive ? "bg-white text-sky-700 shadow-[0_1px_3px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-sky-500" : "text-slate-600 hover:text-slate-900 hover:bg-white/70"}`;

const STATUS_COLOR = { Intake: "text-slate-600", "Documents received": "text-sky-700", Analysis: "text-violet-700", "Memo drafted": "text-emerald-700", "In review": "text-amber-700" };

function TaskLink({ t }) {
  return (
    <NavLink to={`/task/${t.id}`} data-testid={`sidebar-task-${t.id}`}
      className={({ isActive }) => `block px-3 py-2 rounded-xl border transition-colors ${isActive ? "bg-white border-sky-200 shadow-[0_1px_3px_rgba(2,132,199,0.12)]" : "border-transparent hover:bg-white/70"}`}>
      <div className="text-[13px] font-medium text-slate-900 truncate">{t.title}</div>
      <div className="flex justify-between gap-2 text-[11px] mt-0.5">
        <span className="text-slate-500 truncate">{t.subtitle}</span>
        <span className={`shrink-0 ${STATUS_COLOR[t.status] || "text-slate-600"}`}>{t.status}</span>
      </div>
    </NavLink>
  );
}

function Sidebar() {
  const { tasks, refreshTasks } = useApp();
  const nav = useNavigate();
  const loans = tasks.filter((t) => t.type === "loan");
  const wires = tasks.filter((t) => t.type === "wire");
  const start = safe(async (loan_key) => { const { data } = await api.post("/tasks", { type: "loan", loan_key }); await refreshTasks(); nav(`/task/${data.id}`); });
  const reset = async () => { await api.post("/demo/reset"); await refreshTasks(); nav("/"); toast.success("Workspace reset"); setTimeout(() => window.location.reload(), 300); };
  return (
    <aside data-testid="task-sidebar" className="hidden md:flex w-64 shrink-0 border-r border-slate-200/80 bg-slate-50/80 flex-col min-h-0">
      <div className="p-3 space-y-0.5 border-b border-slate-200/70 mb-3">
        <NavLink to="/" end data-testid="sidebar-home-link" className={navCls}>
          <LayoutGrid className="w-4 h-4" />OmniFi Home
        </NavLink>
        <NavLink to="/lending" data-testid="sidebar-lending-link" className={navCls}>
          <Building2 className="w-4 h-4" />Credit Lending hub
        </NavLink>
        <NavLink to="/credit-reports" data-testid="sidebar-credit-reports-link" className={navCls}>
          <BarChart3 className="w-4 h-4" />Credit reports
        </NavLink>
        <NavLink to="/executive" data-testid="sidebar-executive-link" className={navCls}>
          <Presentation className="w-4 h-4" />Executive summary
        </NavLink>
        <NavLink to="/policy" data-testid="sidebar-policy-link" className={navCls}>
          <Scale className="w-4 h-4" />Credit policy
        </NavLink>
        <NavLink to="/grc" data-testid="sidebar-grc-link" className={navCls}>
          <ShieldCheck className="w-4 h-4" />GRC & Audit
        </NavLink>
        <NavLink to="/approvals" data-testid="sidebar-approvals-link" className={navCls}>
          <Inbox className="w-4 h-4" />Approvals
        </NavLink>
        <NavLink to="/settings" data-testid="sidebar-settings-link" className={navCls}>
          <SettingsIcon className="w-4 h-4" />Settings
        </NavLink>
        <NavLink to="/aml" data-testid="sidebar-aml-link" className={navCls}>
          <ShieldCheck className="w-4 h-4" />AML Intelligence
        </NavLink>
        <NavLink to="/fraud" data-testid="sidebar-fraud-link" className={navCls}>
          <ShieldAlert className="w-4 h-4" />Fraud engine
        </NavLink>
        <NavLink to="/monitor" data-testid="sidebar-monitor-link" className={navCls}>
          <Activity className="w-4 h-4" />Live monitoring
        </NavLink>
        <NavLink to="/market-comps" data-testid="sidebar-market-comps-link" className={navCls}>
          <MapPin className="w-4 h-4" />Market comps
        </NavLink>
        <NavLink to="/governance" data-testid="sidebar-governance-link" className={navCls}>
          <Scale className="w-4 h-4" />Governance & assurance
        </NavLink>
        <NavLink to="/systems" data-testid="sidebar-systems-link" className={navCls}>
          <Network className="w-4 h-4" />Systems & controls
        </NavLink>
        <NavLink to="/admin" data-testid="sidebar-admin-link" className={navCls}>
          <Users className="w-4 h-4" />User admin
        </NavLink>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll px-3 space-y-6 pb-4">
        <div>
          <div className="flex items-center justify-between px-3 mb-2">
            <span className="eyebrow flex items-center gap-1.5"><Building2 className="w-3 h-3" />Commercial Lending</span>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="sidebar-new-loan-button" className="text-slate-500 hover:text-sky-600 transition-colors"><Plus className="w-4 h-4" /></DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-slate-300">
                <DropdownMenuItem data-testid="new-loan-dallas" onClick={() => start("dallas")}>Dallas Office Tower</DropdownMenuItem>
                <DropdownMenuItem data-testid="new-loan-frisco" onClick={() => start("frisco")}>Frisco Retail Plaza</DropdownMenuItem>
                <DropdownMenuItem data-testid="new-loan-austin" onClick={() => start("austin")}>Austin Commerce Center ($10M example)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="space-y-1">
            {loans.length ? loans.map((t) => <TaskLink key={t.id} t={t} />) : <p className="px-3 text-xs text-slate-400">No loan tasks yet. Ask the assistant to start one.</p>}
          </div>
        </div>
        <div>
          <div className="px-3 mb-2 eyebrow flex items-center gap-1.5"><ShieldAlert className="w-3 h-3" />Wire Operations & AML</div>
          <div className="space-y-1">{wires.map((t) => <TaskLink key={t.id} t={t} />)}</div>
        </div>
        <div>
          <div className="px-3 mb-2 eyebrow flex items-center gap-1.5"><Globe2 className="w-3 h-3" />Cross-Border Compliance</div>
          <div className="space-y-1">{tasks.filter((t) => t.type === "compliance").map((t) => <TaskLink key={t.id} t={t} />)}</div>
        </div>
        <div>
          <div className="px-3 mb-2 eyebrow flex items-center gap-1.5"><UserCheck className="w-3 h-3" />KYC & Due Diligence</div>
          <div className="space-y-1">{tasks.filter((t) => t.type === "kyc").map((t) => <TaskLink key={t.id} t={t} />)}</div>
        </div>
      </div>
      <div className="mx-3 mt-3 flex gap-1.5" data-testid="demo-script-links">
        <a data-testid="demo-script-pdf" href={`${API}/demo-script.pdf`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-xl py-2 hover:bg-sky-100"><FileText className="w-3 h-3" />Demo script</a>
        <a data-testid="demo-script-docx" href={`${API}/demo-script.docx`} className="flex items-center justify-center gap-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-xl px-3 py-2 hover:border-slate-300" title="Download as Word"><FileDown className="w-3 h-3" />Word</a>
      </div>
      <a data-testid="demo-playbook-pdf" href={`${API}/demo-playbook.pdf`} target="_blank" rel="noreferrer" className="mx-3 mt-1.5 flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-sky-600 rounded-xl py-2 hover:bg-sky-700"><Presentation className="w-3 h-3" />Demo playbook (PDF)</a>
      <button data-testid="reset-demo-button" onClick={reset} className="m-3 mt-1.5 flex items-center justify-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-800 bg-white border border-slate-200 rounded-xl py-2 hover:border-slate-300">
        <RotateCcw className="w-3 h-3" />Reset workspace
      </button>
    </aside>
  );
}

export default function Shell() {
  const [demo, setDemo] = useState(localStorage.getItem("cs-demo-open") === "1");
  const toggle = (v) => { setDemo(v); localStorage.setItem("cs-demo-open", v ? "1" : "0"); };
  return (
    <div className="h-screen flex flex-col bg-[#f6f8fb] text-slate-800 overflow-hidden">
      <Header onDemo={() => toggle(!demo)} />
      <MobileNav />
      {demo && <GuidedDemo onClose={() => toggle(false)} />}
      <div className="flex flex-1 min-h-0">
        <Sidebar />
        <main className="flex-1 min-w-0 min-h-0 flex"><Outlet /></main>
      </div>
    </div>
  );
}
